import { ThunkDispatch } from "@reduxjs/toolkit";
import React from 'react';
import { connect, useDispatch } from "react-redux";

import { imageEditingActions, ImageEditingState, ProcessingHistory } from '../../reducers/imageEditing';

// State型定義
interface RootState {
  imageEditing: ImageEditingState;
  [key: string]: any;
}

// mapStateToPropsの型定義
function mapStateToProps(state: RootState) {
  return {
    selectedImage: state.imageEditing.selectedImage,
    editHistory: state.imageEditing.editHistory,
    currentHistoryIndex: state.imageEditing.currentHistoryIndex,
    showAfter: state.imageEditing.showAfter,
  };
}

interface ImageEditingHistoryPanelProps {
  selectedImage: ImageEditingState['selectedImage'];
  editHistory: ImageEditingState['editHistory'];
  currentHistoryIndex: ImageEditingState['currentHistoryIndex'];
  showAfter: ImageEditingState['showAfter'];
  canUseAIProcessing?: boolean;
}

const ImageEditingHistoryPanel: React.FC<ImageEditingHistoryPanelProps> = (props) => {
  const { selectedImage, editHistory, currentHistoryIndex, showAfter, canUseAIProcessing = true } = props;
  const dispatch = useDispatch<ThunkDispatch<any, any, any>>();

  // 選択された画像の履歴を取得（画像未選択時は空配列）
  const actualHistory = selectedImage ? (editHistory[selectedImage.id] || []) : [];

  // 元画像を履歴の先頭に追加（仮想的に）
  // 画像がアップロードされた時点で「元画像」を表示
  const imageHistory = selectedImage
    ? [
        {
          id: 'original',
          processingType: 'original' as any,
          processedImageUrl: selectedImage.url || '',
          thumbnailUrl: selectedImage.url || '',
          timestamp: 0,
        },
        ...actualHistory,
      ]
    : [];

  // 現在選択されている履歴インデックス（-1は最新）
  const currentIndex = selectedImage ? (currentHistoryIndex[selectedImage.id] ?? -1) : -1;

  // 履歴項目クリックハンドラ
  const handleHistoryClick = (historyIndex: number) => {
    if (!selectedImage) return;

    // 履歴を選択
    dispatch(imageEditingActions.selectHistory({
      imageId: selectedImage.id,
      historyIndex,
    }));

    // Beforeモードの場合、Afterモードに切り替え
    const isAfterMode = showAfter[selectedImage.id] ?? false;
    if (!isAfterMode) {
      dispatch(imageEditingActions.toggleShowAfter(selectedImage.id));
    }
  };

  // 処理タイプの表示名を取得
  const getProcessingTypeName = (history: ProcessingHistory) => {
    if (history.processingType === 'original') {
      return '元画像';
    }
    if (history.processingType === 'blueSky') {
      return '青空加工';
    }
    if (history.processingType === 'mosaic') {
      return '自動モザイク';
    }
    if (history.processingType === 'manualMosaic') {
      return '手動モザイク';
    }
    return '';
  };

  // モザイク対象の表示名を取得
  const getMosaicTargetNames = (history: ProcessingHistory) => {
    if (history.processingType !== 'mosaic' || !history.mosaicTargets) return '';
    return history.mosaicTargets
      .map(target => {
        if (target === 'face') return '人の顔';
        if (target === 'licensePlate') return 'ナンバープレート';
        return target;
      })
      .join(', ');
  };

  // タイムスタンプのフォーマット
  const formatTimestamp = (timestamp: number) => {
    const date = new Date(timestamp);
    const year = date.getFullYear();
    const month = (date.getMonth() + 1).toString().padStart(2, '0');
    const day = date.getDate().toString().padStart(2, '0');
    const hours = date.getHours().toString().padStart(2, '0');
    const minutes = date.getMinutes().toString().padStart(2, '0');
    return `${year}/${month}/${day} ${hours}:${minutes}`;
  };

  return (
    <div className="w-full h-full flex flex-col overflow-hidden">
      {/* 処理履歴セクション */}
      <div className="w-full flex-1 flex flex-col rounded-lg overflow-hidden min-h-0 border-2 border-gray-300 bg-white">
        <div className="text-gray-800 text-center py-2 px-4 font-semibold bg-gray-100 flex-shrink-0">
          編集履歴
        </div>

        {/* コンテンツ部分 */}
        <div className="relative flex-1 min-h-0 overflow-hidden">
          {/* プラン制限オーバーレイ（ヘッダーの下のみ） */}
          {!canUseAIProcessing && (
            <div
              className="absolute inset-0 z-10 flex items-center justify-center"
              style={{ backgroundColor: '#e5e7eb' }}
            >
              <div className="text-center text-gray-700 p-6 max-w-md">
                <p className="font-medium text-base leading-relaxed">
                  Basicプラン以上でご利用いただけます。<br />
                  詳しくは、担当営業もしくは問合せフォームまでご連絡ください。
                </p>
              </div>
            </div>
          )}

          <div className="absolute inset-0 overflow-y-auto p-3 bg-white" style={!canUseAIProcessing ? { visibility: 'hidden' } : {}}>
            {imageHistory.length === 0 ? (
              // 履歴がない場合
              <div className="h-full flex items-center justify-center">
                <div className="text-center text-gray-500">
                  <p className="text-base font-medium mb-2">編集履歴はまだありません</p>
                  <p className="text-sm">画像をアップロードして、AI加工を適用すると</p>
                  <p className="text-sm">編集履歴がここに表示されます</p>
                </div>
              </div>
            ) : (
              // 履歴がある場合
              <>
                <p className="text-xs text-gray-500 mb-3 text-center">
                  クリックして過去の処理結果を表示
                </p>

                <div className="w-full space-y-3">
              {imageHistory.map((history, index) => {
                const isSelected = currentIndex === index || (currentIndex === -1 && index === imageHistory.length - 1);
                const isLatest = index === imageHistory.length - 1;

                return (
                  <div
                    key={history.id}
                    onClick={() => handleHistoryClick(index)}
                    className="flex gap-4 rounded-lg cursor-pointer transition-all p-3"
                    style={
                      isSelected
                        ? {
                            backgroundColor: '#fff7ed',
                            border: '3px solid #f97316',
                            boxShadow: '0 4px 6px -1px rgba(0, 0, 0, 0.1)',
                            minHeight: '160px',
                            maxHeight: '160px',
                          }
                        : {
                            backgroundColor: '#ffffff',
                            border: '2px solid #e5e7eb',
                            minHeight: '160px',
                            maxHeight: '160px',
                          }
                    }
                  >
                    {/* サムネイル画像 */}
                    <div className="flex-shrink-0 flex items-center justify-center bg-gray-100 rounded relative" style={{ width: '128px', height: '128px' }}>
                      <img
                        src={history.thumbnailUrl}
                        alt={`${getProcessingTypeName(history)}の結果`}
                        className="w-full h-full object-contain"
                        onError={(e) => {
                          console.error('画像読み込みエラー:', history.thumbnailUrl);
                          e.currentTarget.style.display = 'none';
                        }}
                        onLoad={() => {
                          console.log('画像読み込み成功:', history.thumbnailUrl);
                        }}
                      />
                      {/* 最新バッジ */}
                      {isLatest && history.processingType !== 'original' && (
                        <div
                          className="absolute top-2 right-2 text-xs font-bold px-3 py-1.5 rounded-full text-white shadow-lg border-2 border-white"
                          style={{ backgroundColor: '#6b7280' }}
                        >
                          最新
                        </div>
                      )}
                    </div>

                    {/* 情報エリア */}
                    <div className="flex-1 flex flex-col justify-center">
                      <p className="text-sm font-bold text-gray-800 mb-2">
                        {getProcessingTypeName(history)}
                      </p>
                      {history.processingType === 'mosaic' && history.mosaicTargets && (
                        <p className="text-xs text-gray-600 mb-2">
                          対象: {getMosaicTargetNames(history)}
                        </p>
                      )}
                      {history.timestamp !== 0 && (
                        <p className="text-xs text-gray-500">{formatTimestamp(history.timestamp)}</p>
                      )}
                    </div>
                  </div>
                );
              }).reverse()}
                </div>
              </>
            )}
          </div>
        </div>
      </div>

      {/* 注意事項 - AI加工機能が利用可能な場合のみ表示 */}
      {canUseAIProcessing && (
        <div className="w-full p-3 bg-gray-50 rounded-lg border border-gray-200 flex-shrink-0" style={{ marginTop: '12px' }}>
          <p className="text-xs text-gray-600">
            <strong>注意:</strong> 履歴は画像ごとに保存されますが、ページをリロードすると消えます。
          </p>
        </div>
      )}
    </div>
  );
};

export default connect(mapStateToProps)(ImageEditingHistoryPanel);
